import { createSignal } from '@react-rxjs/utils';
import {
  Observable,
  catchError,
  combineLatest,
  concat,
  filter,
  map,
  of,
  pairwise,
  startWith,
  switchMap,
  take,
  timer,
} from 'rxjs';
import { request } from './request';
import { CommandState, LockStatus, Vehicle, objectById, vehicle$ } from './vehicle';
import { Task, convertToAsyncTaskState } from '../utils/rxjs/task';
import { bind } from '@react-rxjs/core';
import { distinctUntilDeepChanged } from '../utils/rxjs/misc';
import { isFeatureEnabledInObject$ } from './features';

export type CommandType = 'lock' | 'unlock';

export const [changeLock$, changeLock] = createSignal<CommandType>();
const sendSignal$ = vehicle$.pipe(
  filter(Boolean),
  take(1),
  switchMap(
    ({ id }): Observable<Task<boolean>> =>
      changeLock$.pipe(
        switchMap((signal) =>
          request(`/gateway/device-parameter-manager-service/v20231106/object-lock/${id}/${signal}`, {
            method: 'POST',
          }).pipe(
            map(() => true),
            convertToAsyncTaskState()
          )
        )
      )
  )
);

export interface Lock {
  lockState: {
    commandState: CommandState | 'SUCCESS';
    errorMessage: string | null;
  };
  lockStatus: LockStatus;
  athState: Vehicle['athState'];
}

const defaultLockState: Lock['lockState'] = { commandState: null, errorMessage: null };
const defaultLock: Lock = { lockState: defaultLockState, lockStatus: 'UNKNOWN', athState: null };

const DEFAULT_POLLING_RATE = 10000;
const POLLING_RATE_WHILE_WAITING_FOR_COMMAND = 2000;

/** forceQuickPoll is used on first poll after sending the lock/unlock command
 * to have 2 quick polls after command is sent. this is in case first poll recieves
 * outdated data from the BE and continues to poll in slow mode.
 */
export type VehicleLockInfo = Pick<Vehicle, 'lockState' | 'lockStatus' | 'athState'>;
const pollObject = (forceQuickPoll?: boolean): Observable<VehicleLockInfo> =>
  vehicle$.pipe(
    filter(Boolean),
    take(1),
    switchMap(({ id }) =>
      objectById(id).pipe(
        map((res) => res.items[0]),
        distinctUntilDeepChanged(),
        map(
          ({ athState, lockState, lockStatus }): VehicleLockInfo => ({
            athState,
            lockState,
            lockStatus,
          })
        ),
        catchError(() =>
          of<VehicleLockInfo>({
            lockState: {
              commandState: 'FAILED',
              errorMessage: 'UNKNOWN',
            },
            athState: null,
            lockStatus: null,
          })
        ),
        switchMap((current) => {
          return concat(
            of(current),
            timer(
              current.lockState?.commandState === 'SENT' || forceQuickPoll
                ? POLLING_RATE_WHILE_WAITING_FOR_COMMAND
                : DEFAULT_POLLING_RATE
            ).pipe(switchMap(() => pollObject()))
          );
        })
      )
    )
  );

export const [useLock] = bind(
  combineLatest([isFeatureEnabledInObject$('LOCK_UNLOCK_COMMAND'), vehicle$]).pipe(
    switchMap(([enabled, vehicle]) =>
      !enabled || !vehicle
        ? of(null)
        : sendSignal$.pipe(
            startWith({ running: false, result: null, error: null }),
            switchMap(({ error, running }) =>
              (!running ? pollObject(true) : (of(defaultLock) as Observable<VehicleLockInfo>)).pipe(
                map(({ lockState, lockStatus, athState }) => {
                  let mergedLockState = { ...(lockState ?? defaultLockState) };

                  if (error) {
                    mergedLockState = {
                      commandState: 'FAILED',
                      errorMessage: 'UNKNOWN',
                    };
                  }

                  if (running) {
                    mergedLockState = {
                      commandState: 'SENT',
                      errorMessage: '',
                    };
                  }

                  return {
                    lockState: mergedLockState,
                    lockStatus: running ? ('running' as const) : lockStatus ?? null,
                    athState: running ? ('running' as const) : athState,
                  };
                })
              )
            ),
            startWith(defaultLock),
            pairwise(),
            map(([prev, current]): Lock => {
              const athState =
                current.athState === 'running'
                  ? prev.athState === 'running'
                    ? null
                    : prev.athState
                  : current.athState;

              const lockStatus =
                current.lockStatus === 'running'
                  ? prev.lockStatus === 'running'
                    ? null
                    : prev.lockStatus
                  : current.lockStatus;

              if (
                prev.lockState?.commandState === 'SENT' &&
                current.lockState?.commandState !== 'SENT' &&
                !current.lockState?.errorMessage
              ) {
                return { lockState: { ...current.lockState, commandState: 'SUCCESS' }, athState, lockStatus };
              }
              return { ...current, athState, lockStatus };
            }),
            distinctUntilDeepChanged()
          )
    )
  )
);
